import { formatDate } from "@sembark-travel/datetime-utils"
import {
	Badge,
	Box,
	Button,
	CheckboxInput,
	Heading,
	Icons,
	Inline,
	Select,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	Link,
	NavigationPromptDialog,
	useNavigationPrompt,
} from "@sembark-travel/ui-router"
import {
	arrayMutators,
	Form,
	SelectField,
	useForm,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"
import {
	DEFAULT_BASE_MEAL_PLAN_ORDER,
	IntervalDays,
	MealPlanForm,
	MealRatesSection,
	RoomRatesSection,
	SeasonIntervals,
	buildRoomCols,
	buildRooms,
	compareMealPlanEntriesByPriority,
	computeBlockErrors,
	roudingOptions,
	syncBlockMealRates,
	validateBlockMealRates,
	type IRateBlockSchema,
	type TSeasonFormInterval,
} from "../RateSheets"
import { generatePath } from "../router-utils"
import { IntervalsInDialog } from "./Calendar"
import type { IHotel } from "./store"

// Calendar rates carry no hotel or cancellation/payment policies — those belong
// to a rate sheet — so the rate block shape is used without them.
type TCalendarRatesFormSchema = Omit<
	IRateBlockSchema,
	| "hotel"
	| "cancellation_policy"
	| "cancellation_policy_is_manual"
	| "payment_policy"
	| "payment_policy_is_manual"
>

function buildInitialRateBlock(hotel: IHotel): TCalendarRatesFormSchema {
	return {
		rounding_point: roudingOptions[0],
		pricing_type: { id: "RBP", name: "Room Rate" },
		child_meal_rate_same_as_adult: true,
		is_lower_occupancy_rate_configured: false,
		meal_plans: [],
		meal_rates: [],
		pax_types: [],
		rates: [],
		rooms: buildRooms(hotel.ota_room_types),
	}
}

export function AddCalendarRates({
	hotel,
	intervals: initialIntervals,
}: {
	hotel: IHotel
	intervals: TSeasonFormInterval[]
}) {
	const [intervals, setIntervals] = useState(initialIntervals)
	const blockInitialValues = useMemo(
		() => buildInitialRateBlock(hotel),
		[hotel]
	)
	// Nothing is entered until an interval exists, so there's nothing to lose
	// by navigating away before then.
	const { blocker, disablePrompt } = useNavigationPrompt(intervals.length > 0)

	return (
		<Stack gap="6">
			<NavigationPromptDialog
				blocker={blocker}
				title="You have unsaved rates for this calendar. Are you sure you want to leave? All changes will be lost."
			/>
			<Form<TCalendarRatesFormSchema>
				mutators={{ ...arrayMutators }}
				initialValues={blockInitialValues}
				onSubmit={() => undefined}
				validate={validateBlockMealRates}
				subscription={{ values: true }}
			>
				{({ values }) => (
					<AddCalendarRatesForm
						hotel={hotel}
						intervals={intervals}
						values={values}
						setIntervals={setIntervals}
						onSetRates={disablePrompt}
					/>
				)}
			</Form>
		</Stack>
	)
}

function AddCalendarRatesForm({
	hotel,
	intervals,
	values,
	setIntervals,
	onSetRates,
}: {
	hotel: IHotel
	intervals: TSeasonFormInterval[]
	values: TCalendarRatesFormSchema
	setIntervals: React.Dispatch<React.SetStateAction<TSeasonFormInterval[]>>
	onSetRates: () => void
}) {
	const form = useForm<TCalendarRatesFormSchema>()
	const roomTypes = useMemo(() => hotel.ota_room_types || [], [hotel])
	const mealPlans = useMemo(() => hotel.ota_meal_plans || [], [hotel])
	const roomCols = useMemo(() => buildRoomCols(hotel), [hotel])

	const handleUpdateMealPlans = useCallback(
		(updated: TCalendarRatesFormSchema["meal_plans"]) => {
			const sorted = [...updated].sort(compareMealPlanEntriesByPriority)
			const currentMealRates = form.getState().values.meal_rates || []
			const nextMealRates = syncBlockMealRates(sorted, currentMealRates)
			form.batch(() => {
				form.change("meal_plans", sorted)
				form.change("meal_rates", nextMealRates)
			})
		},
		[form]
	)

	const handleUpdateRooms = useCallback(
		(rooms: TCalendarRatesFormSchema["rooms"]) => {
			form.change("rooms", rooms)
		},
		[form]
	)

	const handleUpdateMealRate = useCallback(
		(
			mealRates: TCalendarRatesFormSchema["meal_rates"],
			childSameAsAdult: TCalendarRatesFormSchema["child_meal_rate_same_as_adult"]
		) => {
			form.batch(() => {
				form.change("meal_rates", mealRates)
				form.change("child_meal_rate_same_as_adult", childSameAsAdult)
			})
		},
		[form]
	)

	const handleUpdateRatesAndPaxTypes = useCallback(
		(
			rates: TCalendarRatesFormSchema["rates"],
			paxTypes: TCalendarRatesFormSchema["pax_types"],
			isLowerOccupancyConfigured?: boolean
		) => {
			form.batch(() => {
				form.change("rates", rates)
				form.change("pax_types", paxTypes)
				if (isLowerOccupancyConfigured !== undefined) {
					form.change(
						"is_lower_occupancy_rate_configured",
						isLowerOccupancyConfigured
					)
				}
			})
		},
		[form]
	)

	// Auto-selects a base meal plan once, same as RateSheets' HotelRateForm —
	// a block needs exactly one base plan before it can price anything.
	useEffect(() => {
		if (mealPlans.length === 0) return
		const hasBase = (values.meal_plans || []).some((mt) => Boolean(mt?.is_base))
		if (hasBase) return
		let defaultMealPlan = mealPlans.find((mp) =>
			DEFAULT_BASE_MEAL_PLAN_ORDER.includes(
				(mp.meal_category || mp.name || "").trim().toUpperCase()
			)
		)
		if (!defaultMealPlan) defaultMealPlan = mealPlans[0]
		if (!defaultMealPlan) return
		handleUpdateMealPlans([
			...(values.meal_plans || []),
			{
				ota_meal_plan: defaultMealPlan,
				category: defaultMealPlan.meal_category || undefined,
				is_base: true,
				includes_all_room_types: true,
				ota_room_types: [],
			},
		])
	}, [mealPlans, values.meal_plans, handleUpdateMealPlans])

	const selectedRoomTypeIds = useMemo(
		() => new Set((values.rooms || []).map((r) => Number(r.ota_room_type))),
		[values.rooms]
	)

	const selectedRoomTypes = useMemo(
		() => roomTypes.filter((rt) => selectedRoomTypeIds.has(Number(rt.id))),
		[roomTypes, selectedRoomTypeIds]
	)

	const handleToggleRoomType = useCallback(
		(roomType: THotelOtaRoomType, checked: boolean) => {
			const nextIds = new Set(selectedRoomTypeIds)
			if (checked) {
				nextIds.add(Number(roomType.id))
			} else {
				nextIds.delete(Number(roomType.id))
			}
			handleUpdateRooms(
				buildRooms(roomTypes.filter((rt) => nextIds.has(Number(rt.id))))
			)
		},
		[roomTypes, selectedRoomTypeIds, handleUpdateRooms]
	)

	const hotelErrors = computeBlockErrors(values, hotel)
	const hasRoomTypes = (values.rooms || []).length > 0
	const canSubmit =
		hasRoomTypes &&
		intervals.length > 0 &&
		Object.keys(hotelErrors).length === 0
	let submitButtonTitle: string | undefined
	if (intervals.length === 0) {
		submitButtonTitle = "Please add at least one interval"
	} else if (Object.keys(hotelErrors).length > 0) {
		submitButtonTitle = "Please fix the errors in the form"
	}

	function handleSetRates() {
		if (!canSubmit) return
		// The grid already rounds `rate_before_tax` per `rounding_point`, and with
		// no API to send the derivation to yet, the unrounded figure has nowhere
		// to go — so it's dropped here rather than carried along unused. Mirrors
		// RateSheets' getPayloadData, which normalizes rate fields the same way.
		const rates = (values.rates || []).map(
			({ rate_before_tax_unrounded: _unrounded, ...rate }) => ({
				...rate,
				rate_before_tax:
					rate.rate_before_tax != null ? Number(rate.rate_before_tax) : null,
			})
		)
		console.log({
			hotel_id: hotel.id,
			intervals,
			...values,
			rates,
		})
		showSnackbar("Rates logged to the console")
		// There's no save to wait on yet, so the prompt is cleared right away —
		// same as RateSheets clears it once its own save actually goes through.
		onSetRates()
	}

	return (
		<Stack>
			<Inline
				paddingX="6"
				paddingY="3"
				justifyContent="between"
				alignItems="center"
				borderBottomWidth="1"
			>
				<Link
					to={generatePath("/hotels/:hotelId/calendar", {
						hotelId: String(hotel.id),
					})}
					fontSize="sm"
					color="muted"
				>
					<Icons.ArrowLeft /> Back to Calendar
				</Link>
				<Heading as="h4">Set Rates in Calendar</Heading>
				<ConfirmRatesInDialog
					roomTypes={selectedRoomTypes}
					mealPlans={values.meal_plans}
					intervals={intervals}
					onConfirm={handleSetRates}
				>
					{({ open }) => (
						<Button
							title={submitButtonTitle}
							level="primary"
							disabled={!canSubmit}
							onClick={open}
						>
							Set Rates
						</Button>
					)}
				</ConfirmRatesInDialog>
			</Inline>
			<Stack>
				<Inline
					justifyContent="between"
					alignItems="center"
					paddingY="3"
					paddingX="6"
				>
					<Text fontWeight="semibold">Set Interval</Text>
					<IntervalsInDialog
						title="Update Intervals"
						initialIntervals={intervals.length > 0 ? intervals : undefined}
						onSuccess={setIntervals}
					>
						{({ open }) => (
							<Button level="secondary" size="sm" type="button" onClick={open}>
								{intervals.length > 0 ? <Icons.Pencil /> : <Icons.Plus />}
								{intervals.length > 0 ? "Edit" : "Add"} Intervals
							</Button>
						)}
					</IntervalsInDialog>
				</Inline>
				<SeasonIntervals intervals={intervals} />
			</Stack>
			<Stack gap="8" paddingX="6" paddingY="4">
				<Inline>
					<SelectField
						select={Select}
						label="Round up to"
						name="rounding_point"
						placeholder="Select rounding"
						maxWidth="xs"
						options={roudingOptions}
					/>
				</Inline>
				<Inline gap="6" flexWrap="wrap">
					<RoomTypeSelector
						roomTypes={roomTypes}
						selectedIds={selectedRoomTypeIds}
						onToggle={handleToggleRoomType}
					/>
					{mealPlans.length > 0 ? (
						<Stack gap="3" flex="1" maxWidth="lg">
							<Box paddingX="4">
								<Text fontWeight="semibold" fontSize="md">
									Applicable Meal Plans
								</Text>
							</Box>
							<Stack
								borderWidth="1"
								borderColor="emphasis"
								rounded="lg"
								bgColor="default"
							>
								<MealPlanForm
									hotelId={hotel.id}
									mealPlans={mealPlans}
									parentMealPlans={values.meal_plans || []}
									onUpdateMealPlans={handleUpdateMealPlans}
								/>
							</Stack>
						</Stack>
					) : (
						<Text color="muted" fontSize="md" fontWeight="semibold">
							No meal plans available for this hotel
						</Text>
					)}
					<MealRatesSection
						currency={hotel.currency}
						parentMealRates={values.meal_rates}
						selectedMealPlans={values.meal_plans}
						initialBlockMealRates={[]}
						childRateSameAsAdult={values.child_meal_rate_same_as_adult}
						onUpdateMealRate={handleUpdateMealRate}
					/>
				</Inline>
				{roomTypes.length > 0 ? (
					<RoomRatesSection
						hotel={hotel}
						roomCols={roomCols}
						roomTypes={roomTypes}
						hotelErrors={hotelErrors}
						parentMealPlans={values.meal_plans}
						parentMealRates={values.meal_rates}
						parentRates={values.rates}
						parentPaxTypes={values.pax_types}
						parentRooms={values.rooms}
						parentRoundingPoint={values.rounding_point}
						parentIsLowerOccupancyRateConfigured={
							values.is_lower_occupancy_rate_configured
						}
						initialBlockRates={[]}
						initialBlockPaxTypes={[]}
						initialBlockRooms={[]}
						childRateSameAsAdult={values.child_meal_rate_same_as_adult}
						onUpdateRooms={handleUpdateRooms}
						onUpdateRatesAndPaxTypes={handleUpdateRatesAndPaxTypes}
					/>
				) : (
					<Text color="muted" fontSize="md" fontWeight="semibold">
						No Room Types available for this hotel
					</Text>
				)}
			</Stack>
		</Stack>
	)
}

function ConfirmRatesInDialog({
	roomTypes,
	mealPlans,
	intervals,
	onConfirm,
	children,
}: {
	roomTypes: THotelOtaRoomType[]
	mealPlans: TCalendarRatesFormSchema["meal_plans"]
	intervals: TSeasonFormInterval[]
	onConfirm: () => void
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()

	function handleConfirm() {
		onConfirm()
		close()
	}

	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} sm>
				<DialogHeader title="Set Rates in Calendar" />
				<DialogBody>
					<ConfirmRatesSummary
						roomTypes={roomTypes}
						mealPlans={mealPlans}
						intervals={intervals}
					/>
				</DialogBody>
				<DialogFooter>
					<Inline justifyContent="end">
						<Button level="primary" onClick={handleConfirm}>
							Confirm
						</Button>
					</Inline>
				</DialogFooter>
			</Dialog>
		</>
	)
}

function ConfirmRatesSummary({
	roomTypes,
	mealPlans,
	intervals,
}: {
	roomTypes: THotelOtaRoomType[]
	mealPlans: TCalendarRatesFormSchema["meal_plans"]
	intervals: TSeasonFormInterval[]
}) {
	return (
		<Stack gap="6">
			<Stack gap="2">
				<Text fontWeight="semibold">Rooms</Text>
				<Stack as="ul" gap="2">
					{roomTypes.map((rt) => (
						<SummaryListItem key={rt.id}>
							<Text>{rt.name}</Text>
						</SummaryListItem>
					))}
				</Stack>
			</Stack>
			<Stack gap="2">
				<Text fontWeight="semibold">Meal Plans</Text>
				<Stack as="ul" gap="2">
					{mealPlans.map((mp) => (
						<SummaryListItem key={mp.ota_meal_plan.id}>
							<Text>{mp.ota_meal_plan.name}</Text>
							{mp.is_base ? <Badge size="sm">Base</Badge> : null}
						</SummaryListItem>
					))}
				</Stack>
			</Stack>
			<Stack gap="2">
				<Text fontWeight="semibold">Intervals</Text>
				<Stack as="ul" gap="2">
					{intervals.map((interval, index) => (
						<SummaryListItem key={index}>
							{interval.from && interval.to ? (
								<Text>
									{formatDate(interval.from, "DD MMM, YYYY")} &rarr;{" "}
									{formatDate(interval.to, "DD MMM, YYYY")}
								</Text>
							) : null}
							<IntervalDays days={interval.days} />
						</SummaryListItem>
					))}
				</Stack>
			</Stack>
		</Stack>
	)
}

function SummaryListItem({ children }: { children: React.ReactNode }) {
	return (
		<Inline as="li" gap="3" alignItems="center" flexWrap="wrap">
			<Box size="2" rounded="full" bgColor="primary_emphasis" />
			{children}
		</Inline>
	)
}

function RoomTypeSelector({
	roomTypes,
	selectedIds,
	onToggle,
}: {
	roomTypes: THotelOtaRoomType[]
	selectedIds: Set<number>
	onToggle: (roomType: THotelOtaRoomType, checked: boolean) => void
}) {
	return (
		<Stack gap="3" style={{ width: "14rem" }}>
			<Box paddingX="4">
				<Text fontWeight="semibold" fontSize="md">
					Room Type
				</Text>
			</Box>
			<Stack
				borderWidth="1"
				borderColor="emphasis"
				rounded="lg"
				bgColor="default"
				padding="4"
				gap="3"
			>
				{roomTypes.map((rt) => {
					const id = Number(rt.id)
					const checked = selectedIds.has(id)
					const isOnlySelected = checked && selectedIds.size === 1
					const inputId = `room-type-${id}`
					return (
						<Inline key={rt.id} gap="2" alignItems="center">
							<CheckboxInput
								id={inputId}
								checked={checked}
								disabled={isOnlySelected}
								title={
									isOnlySelected
										? "At least one room type is required"
										: undefined
								}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									onToggle(rt, e.target.checked)
								}
							/>
							<Text as="label" htmlFor={inputId}>
								{rt.name}
							</Text>
						</Inline>
					)
				})}
			</Stack>
		</Stack>
	)
}
